import React, { useState, useEffect, useRef } from 'react';
import {
  Bell, AlertTriangle, Info, CheckCircle, X, Clock, ShoppingCart,
  CheckCheck, Package, Sparkles,
} from 'lucide-react';
import { fetchOrderNotificationsFromOrders } from '../api';

const FILTER_TABS = [
  { id: 'all', label: 'All', icon: Bell, accent: 'moss' },
  { id: 'alert', label: 'Alerts', icon: AlertTriangle, accent: 'tiger' },
  { id: 'warning', label: 'Warnings', icon: AlertTriangle, accent: 'earth' },
  { id: 'order', label: 'Orders', icon: ShoppingCart, accent: 'moss' },
];

const TYPE_STYLES = {
  warning: {
    icon: AlertTriangle,
    iconWrap: 'bg-earth/15 text-tiger',
    card: 'border-earth/30 bg-earth/5',
    dot: 'bg-earth',
  },
  alert: {
    icon: AlertTriangle,
    iconWrap: 'bg-tiger/15 text-tiger',
    card: 'border-tiger/25 bg-tiger/5',
    dot: 'bg-tiger',
  },
  info: {
    icon: Info,
    iconWrap: 'bg-moss/15 text-moss',
    card: 'border-moss/25 bg-moss/5',
    dot: 'bg-moss',
  },
  order: {
    icon: ShoppingCart,
    iconWrap: 'bg-moss/15 text-moss',
    card: 'border-moss/30 bg-moss/[0.07]',
    dot: 'bg-moss',
  },
  success: {
    icon: CheckCircle,
    iconWrap: 'bg-moss/15 text-moss',
    card: 'border-moss/25 bg-moss/5',
    dot: 'bg-moss',
  },
  default: {
    icon: Bell,
    iconWrap: 'bg-pakistan/10 text-pakistan/60',
    card: 'border-moss/15 bg-white/40',
    dot: 'bg-pakistan/40',
  },
};

const getTypeStyle = (type) => TYPE_STYLES[type] || TYPE_STYLES.default;

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const audioRef = useRef(null);
  const seenIdsRef = useRef(new Set());
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('readNotificationIds') || '[]');
      if (Array.isArray(saved)) return new Set(saved.map(String));
    } catch (_) {}
    return new Set();
  });

  useEffect(() => {
    let cancelled = false;

    const load = async (playSoundOnNew = false) => {
      try {
        const list = await fetchOrderNotificationsFromOrders();
        if (cancelled) return;

        const newOnes = list.filter((n) => !seenIdsRef.current.has(String(n.id)));
        list.forEach((n) => seenIdsRef.current.add(String(n.id)));

        setNotifications(list);
        if (playSoundOnNew && newOnes.length > 0 && audioRef.current) {
          audioRef.current.play().catch(() => {});
        }
      } catch (e) {
        console.error('Failed to load notifications', e);
      }
    };

    load(false);
    const timer = setInterval(() => load(true), 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const persistRead = (next) => {
    try {
      localStorage.setItem('readNotificationIds', JSON.stringify(Array.from(next).slice(-1000)));
    } catch (_) {}
  };

  const markAsRead = (id) => {
    const sid = String(id);
    setReadIds((prev) => {
      if (prev.has(sid)) return prev;
      const next = new Set(prev);
      next.add(sid);
      persistRead(next);
      return next;
    });
  };

  const markAllAsRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      notifications.forEach((n) => next.add(String(n.id)));
      persistRead(next);
      return next;
    });
  };

  const filteredNotifications = filter === 'all'
    ? notifications
    : notifications.filter((n) => n.type === filter);

  const unreadCount = notifications.filter((n) => !readIds.has(String(n.id))).length;

  const notificationCounts = {
    all: notifications.length,
    alert: notifications.filter((n) => n.type === 'alert').length,
    warning: notifications.filter((n) => n.type === 'warning').length,
    order: notifications.filter((n) => n.type === 'order').length,
  };

  return (
    <div className="ui-page animate-fade-in">
      <audio ref={audioRef} preload="auto">
        <source src="data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAA4T0qEj0AAAAAAAAAAAAAAAAAAAAAP/7kGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/7kGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVQ==" type="audio/mpeg" />
      </audio>

      {/* Header */}
      <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="ui-page-title text-gradient flex items-center gap-2.5">
            <Bell size={28} className="text-moss shrink-0" />
            Notifications
          </h1>
          <p className="ui-page-subtitle">
            Real-time order alerts and activity updates from your store.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {unreadCount > 0 && (
            <span className="badge-warning">
              {unreadCount} unread
            </span>
          )}
          <button type="button" onClick={markAllAsRead} className="ui-btn-outline inline-flex items-center gap-2">
            <CheckCheck size={16} />
            Mark all read
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {FILTER_TABS.map((tab) => {
          const isActive = filter === tab.id;
          const Icon = tab.icon;
          const count = notificationCounts[tab.id] ?? 0;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setFilter(tab.id)}
              className={`ui-card ui-card-pad text-left transition-all duration-200 ${
                isActive ? 'ring-2 ring-tiger/40 shadow-card-hover -translate-y-0.5' : 'ui-card-hover opacity-90 hover:opacity-100'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                  isActive ? 'bg-tiger/15 text-tiger' : 'bg-moss/10 text-moss'
                }`}>
                  <Icon size={18} />
                </span>
                {isActive && (
                  <span className="h-2 w-2 rounded-full bg-tiger shadow-[0_0_8px_#BC6C25]" />
                )}
              </div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-pakistan/50">{tab.label}</p>
              <p className="mt-0.5 text-2xl font-extrabold text-pakistan">{count}</p>
            </button>
          );
        })}
      </div>

      {/* Notification list */}
      <div className="mb-8 space-y-3">
        {filteredNotifications.map((notification) => {
          const isUnread = !readIds.has(String(notification.id));
          const style = getTypeStyle(notification.type);
          const Icon = style.icon;

          return (
            <div
              key={notification.id}
              role="button"
              tabIndex={0}
              onClick={() => markAsRead(notification.id)}
              onKeyDown={(e) => e.key === 'Enter' && markAsRead(notification.id)}
              className={`ui-card ui-card-pad cursor-pointer transition-all duration-200 hover:shadow-card-hover hover:-translate-y-0.5 border ${
                isUnread ? `${style.card} ring-1 ring-tiger/15` : 'border-moss/10 opacity-80'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${style.iconWrap}`}>
                  <Icon size={20} />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start gap-2 mb-1">
                    <h3 className={`font-bold text-pakistan ${isUnread ? 'text-base' : 'text-sm'}`}>
                      {notification.title || notification.message}
                    </h3>
                    {isUnread && (
                      <span className="badge-primary-alt inline-flex items-center gap-1">
                        <Sparkles size={10} />
                        New
                      </span>
                    )}
                  </div>

                  {notification.details && notification.details.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {notification.details.slice(0, 4).map((d, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-pakistan/65">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-earth" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-medium text-pakistan/45">
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={13} className="text-moss/60" />
                      {notification.exactDate && notification.exactTime
                        ? `${notification.exactDate} · ${notification.exactTime}`
                        : notification.time}
                    </span>
                    {notification.time && notification.exactDate && (
                      <span className="text-pakistan/35">({notification.time})</span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(notification.id);
                  }}
                  className="ui-modal-close shrink-0"
                  aria-label="Dismiss"
                >
                  <X size={16} />
                </button>
              </div>

              {isUnread && (
                <div className={`mt-3 h-0.5 w-full rounded-full ${style.dot} opacity-30`} />
              )}
            </div>
          );
        })}

        {filteredNotifications.length === 0 && (
          <div className="ui-empty">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-moss/10">
              <Bell size={32} className="text-moss/40" />
            </div>
            <p className="ui-empty-title">No notifications found</p>
            <p className="mt-1 text-xs text-pakistan/40">
              {filter === 'all' ? 'New order alerts will appear here.' : `No ${filter} notifications at the moment.`}
            </p>
          </div>
        )}
      </div>

      {/* Settings */}
      <div className="ui-card ui-card-pad">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-earth/15 text-tiger">
            <Package size={18} />
          </span>
          <div>
            <h2 className="ui-section-title text-lg">Alert Preferences</h2>
            <p className="text-xs text-pakistan/50 font-medium mt-0.5">Choose which notifications you receive</p>
          </div>
        </div>

        <div className="divide-y divide-moss/10">
          {[
            { title: 'Low Stock Alerts', desc: 'Get notified when products are running low' },
            { title: 'Expiry Warnings', desc: 'Get notified about products nearing expiry' },
            { title: 'Order Updates', desc: 'Get notified about new orders and status changes' },
          ].map((item) => (
            <div key={item.title} className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0">
              <div>
                <p className="font-bold text-sm text-pakistan">{item.title}</p>
                <p className="text-xs text-pakistan/50 mt-0.5">{item.desc}</p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input type="checkbox" className="peer sr-only" defaultChecked />
                <div className="h-6 w-11 rounded-full bg-moss/20 transition-colors peer-checked:bg-tiger peer-focus:outline-none after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-cornsilk after:shadow-sm after:transition-all peer-checked:after:translate-x-5" />
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Notifications;
